import { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from './state'
import {
  getClaims as getClaimsAPI,
  getClaim as getClaimAPI,
  deleteClaimConcept,
  createClaimConcept as createClaimAPI,
  updateClaimConcept as updateClaimAPI,
  uploadAdditionalClaimAttachment as uploadAdditionalClaimAttachmentAPI,
  removeAdditionalClaimAttachment as removeAdditionalClaimAttachment,
  storeClaim as storeClaimAPI
} from '../api/claims'
import {
  setClaims,
  updateClaim,
  deleteClaim as deleteClaimRedux,
  setConceptClaim,
  setCurrentStep,
  resetClaimDamageForm,
  setLatestClaimId,
  setConceptIsLoading,
  setFileIsLoading,
  setClaimIsLoading
} from '../state/slices/claims_slice'
import { IClaim } from '../models/claim'
import convertToBase64 from '../utils/convertToBase64'
import { getResourceAuthorization } from '../state/selectors/resource_authorization'
import { getConceptClaim } from '../state/selectors/claims'

export const useClaims = () => {
  const dispatch = useAppDispatch()
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([])
  const [deletingFiles, setDeletingFiles] = useState<string[]>([])
  const currentResourceAuthorization = useAppSelector(getResourceAuthorization)
  const claimConcept = useAppSelector(getConceptClaim)
  const [uploadErrors, setUploadErrors] = useState<string[]>([])

  useEffect(() => () => {
    dispatch(setClaimIsLoading(false))
    dispatch(setConceptIsLoading(false))
  }, [dispatch])

  const getClaims = async (): Promise<boolean> => {
    dispatch(setClaimIsLoading(true))
    try {
      const result = await getClaimsAPI(currentResourceAuthorization?.relationNumber ?? 0)

      if (result.data) {
        dispatch(setClaims(result.data.sort((a, b) => {
          if (!a.dateOfLoss) return -1
          if (!b.dateOfLoss) return 1

          return new Date(b.dateOfLoss).getTime() - new Date(a.dateOfLoss).getTime()
        })))
      }

      return true
    } catch (error) {
      return false
    } finally {
      dispatch(setClaimIsLoading(false))
    }
  }

  const createClaimConcept = async (
    claim: Pick<
      IClaim,
      'agentNumber' | 'relationNumber' | 'branche' | 'policyNumber'
    >
  ) => {
    try {
      dispatch(setConceptIsLoading(true))
      const result = await createClaimAPI(claim)

      if (result.data) {
        dispatch(setConceptClaim(result.data))
      }

      return result.data
    }  catch (error) {
      return null
    } finally {
      dispatch(setConceptIsLoading(false))
    }
  }

  const updateClaimConcept = async (claim: IClaim) => {
    try {
      dispatch(setConceptIsLoading(true))
      const result = await updateClaimAPI(claim)

      if (result.data) {
        dispatch(setConceptClaim(result.data))
      }

      return result.data
    }  catch (error) {
      return null
    } finally {
      dispatch(setConceptIsLoading(false))
    }
  }

  const storeClaim = async (
    claim: Pick<
      IClaim,
      'id' | 'policyNumber' | 'dateOfLoss' | 'text' | 'phoneNumber'
    >
  ) => {
    try {
      const result = await storeClaimAPI({ ...claim, relationNumber: currentResourceAuthorization?.relationNumber ?? 0 })

      if (result.data) {
        dispatch(updateClaim(result.data))
        dispatch(setConceptClaim(null))
        dispatch(setCurrentStep(3))
        dispatch(resetClaimDamageForm())
        dispatch(setLatestClaimId(result.data.id))
      }

      return result.data
    } catch (error) {
      return null
    }
  }

  const getClaim = async (id: string) => {
    try {
      dispatch(setClaimIsLoading(true))
      const result = await getClaimAPI(currentResourceAuthorization?.relationNumber ?? 0, id)

      if (result.data) {
        dispatch(updateClaim(result.data))
      }

      return true
    } catch (error) {
      return false
    } finally {
      dispatch(setClaimIsLoading(false))
    }
  }

  const deleteClaim = async (id: string) => {
    try {
      const result = await deleteClaimConcept(currentResourceAuthorization?.relationNumber ?? 0, id)

      if (result) {
        dispatch(deleteClaimRedux(id))

        if (id === claimConcept?.id) {
          dispatch(setConceptClaim(null))
        }
      }

      return true
    } catch (error) {
      return false
    }
  }

  const uploadClaimAttachmentFiles = async (claimId: string, files: File[]) => {
    setUploadErrors([])
    const uploads = Array.from(files).map(file =>
      uploadClaimAttachmentFile(claimId, file)
    )

    const results = await Promise.allSettled(uploads)
    const errors: string[] = []

    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        errors.push(result.reason?.message || `Bestand ${files[index].name} kon niet worden geüpload.`)
      }
    })

    setUploadErrors(errors)
  }

  const uploadClaimAttachmentFile = async (claimId: string, file: File) => {
    const base64 = await convertToBase64(file)

    return await uploadClaimAttachmentBase64(claimId, base64 as string, file.name)
  }

  const uploadClaimAttachmentBase64s = async (claimId: string, base64s: { base64: string; fileName: string }[]) => {
    setUploadErrors([])
    const uploads = base64s.map(item =>
      uploadClaimAttachmentBase64(claimId, item.base64, item.fileName)
    )

    const results = await Promise.allSettled(uploads)
    const errors: string[] = []

    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        errors.push(result.reason?.message || `Bestand ${base64s[index].fileName} kon niet worden geüpload.`)
      }
    })

    setUploadErrors(errors)
  }

  const uploadClaimAttachmentBase64 = async (claimId: string, base64: string, fileName: string) => {
    dispatch(setFileIsLoading(true))
    setUploadingFiles((prev) => [...prev, fileName])
    try {
      const result = await uploadAdditionalClaimAttachmentAPI(currentResourceAuthorization?.relationNumber ?? 0, claimId, base64, fileName)

      dispatch(setFileIsLoading(false))
      if (result.data) {
        const claim = await getClaimAPI(currentResourceAuthorization?.relationNumber ?? 0, claimId)

        if (claim.data.status === 'Concept') {
          dispatch(setConceptClaim(claim.data))
        }
      }

      return true
    } catch (e) {
      return false
    } finally {
      setUploadingFiles((prev) => prev.filter((file) => file !== fileName))
      dispatch(setFileIsLoading(false))
    }
  }

  const deleteClaimAttachment = async (fileUrl: string, claimId: string) => {
    try {
      setDeletingFiles((prev) => [...prev, fileUrl])
      const result = await removeAdditionalClaimAttachment(fileUrl)

      if (result) {
        const claim = await getClaimAPI(currentResourceAuthorization?.relationNumber ?? 0, claimId)

        if (claim.data.status === 'Concept') {
          dispatch(setConceptClaim(claim.data))
        }
      }

      return true
    } catch (e) {
      return false
    } finally {
      setDeletingFiles((prev) => prev.filter((file) => file !== fileUrl))
    }
  }

  return {
    getClaims,
    getClaim,
    deleteClaim,
    createClaimConcept,
    uploadClaimAttachmentFiles,
    uploadClaimAttachmentFile,
    uploadClaimAttachmentBase64s,
    uploadClaimAttachmentBase64,
    updateClaimConcept,
    deleteClaimAttachment,
    storeClaim,
    uploadingFiles,
    deletingFiles,
    uploadErrors
  }
}

export default useClaims
